/**
 * Main Application Component
 *
 * Handles routing and global layout.
 * Desktop: Left sidebar navigation
 * Mobile: Bottom navigation
 *
 * Auth: Shows LoginPage when not authenticated.
 */

import * as Sentry from '@sentry/react';
import { Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@services/auth';
import { useOwnerAccess } from '@/hooks/useOwnerAccess';
import { accountService } from '@services/accounts';
import { scheduledTransactionService } from '@services/scheduledTransactions';

// Pages
import {
  AccountsPage,
  TransactionsPage,
  OverviewPage,
  AnalyticsPage,
  CryptoPage,
  CategoriesPage,
  SettingsPage,
  ImportDataPage,
  DevPage,
  MetricsDevPage,
  LogPage,
  LoginPage,
  MorePage,
  PrivacyPage,
  TermsPage,
  // Showcase hub + Depth Cinema winners (others archived to archived_showcases.tar.gz)
  xp1_LandingIndex as Xp1LandingIndex,
  xp1_HybridD as Xp1HybridD,
  xp1_HybridD_v2 as Xp1HybridDv2,
  xp1_HybridD_v3 as Xp1HybridDv3,
  // Feature pages
  FeaturesPage,
  Features_BentoA,
} from '@/pages';
import { QuickAddModal, MoreMenu, AddAccountModal, MetricsTracker } from '@/components';
import { useLanguage } from '@/i18n';
import { FunnelPreview, PricingPageB } from '@/components/upgrade';

// Placeholder for pages not yet implemented
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="empty">
      <div className="empty-icon">?</div>
      <div className="empty-title">{title}</div>
      <div className="empty-text">This page is under construction.</div>
    </div>
  );
}

// Context-aware quick-add: the FAB/sidebar button adds what the current
// page manages (accounts on /accounts, categories on /categories), and its
// label says so - the old label always claimed "Add Transaction" even
// where it opened Add Account.
function quickAddLabelKey(pathname: string): string {
  if (pathname === '/accounts') return 'accounts.add.title';
  if (pathname === '/categories') return 'categories.add.title';
  return 'nav.add_transaction';
}

// Sidebar Navigation (Desktop)
function Sidebar({ onQuickAddClick, isOwner }: { onQuickAddClick: () => void; isOwner: boolean }) {
  const { t } = useLanguage();
  const location = useLocation();
  return (
    <aside className="sidebar">
      <div className="sidebar-logo">NinjaCash</div>
      <nav className="sidebar-nav">
        <NavLink to="/accounts" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
          <span className="sidebar-icon">💳</span>
          <span>{t('nav.accounts')}</span>
        </NavLink>
        <NavLink to="/transactions" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
          <span className="sidebar-icon">📋</span>
          <span>{t('nav.transactions')}</span>
        </NavLink>
        <NavLink to="/overview" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
          <span className="sidebar-icon">📊</span>
          <span>{t('nav.overview')}</span>
        </NavLink>
        <NavLink to="/categories" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
          <span className="sidebar-icon">🏷️</span>
          <span>{t('nav.categories')}</span>
        </NavLink>
        <NavLink to="/analytics" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
          <span className="sidebar-icon">📈</span>
          <span>{t('nav.analytics')}</span>
        </NavLink>
        <NavLink to="/crypto" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
          <span className="sidebar-icon">🪙</span>
          <span>{t('nav.crypto')}</span>
        </NavLink>
        <NavLink to="/settings" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
          <span className="sidebar-icon">⚙️</span>
          <span>{t('nav.settings')}</span>
        </NavLink>
        <NavLink to="/import" className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
          <span className="sidebar-icon">📥</span>
          <span>{t('nav.import')}</span>
        </NavLink>
        {/* Dev links - owner only (nova223@gmail.com) */}
        {isOwner && (
          <>
            <div className="sidebar-divider" />
            <NavLink to="/dev" className={({ isActive }) => `sidebar-item sidebar-dev ${isActive ? 'active' : ''}`}>
              <span className="sidebar-icon">🛠️</span>
              <span>Dev Tools</span>
            </NavLink>
            <NavLink to="/metrics_dev" className={({ isActive }) => `sidebar-item sidebar-dev ${isActive ? 'active' : ''}`}>
              <span className="sidebar-icon">M</span>
              <span>Dev Metrics</span>
            </NavLink>
            <NavLink to="/log" className={({ isActive }) => `sidebar-item sidebar-dev ${isActive ? 'active' : ''}`}>
              <span className="sidebar-icon">📜</span>
              <span>Logs</span>
            </NavLink>
          </>
        )}
      </nav>
      <div className="sidebar-footer">
        <button className="btn btn-primary btn-block" onClick={onQuickAddClick}>
          + {t(quickAddLabelKey(location.pathname))}
        </button>
      </div>
    </aside>
  );
}

// Bottom Navigation (Mobile)
// Note: FAB (floating action button) handles adding transactions - no need for Add in nav
function BottomNav({ onMoreClick }: { onMoreClick: () => void }) {
  const { t } = useLanguage();
  return (
    <nav className="bottom-nav">
      <NavLink to="/accounts" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <span className="nav-icon">💳</span>
        <span className="nav-label">{t('nav.accounts')}</span>
      </NavLink>
      <NavLink to="/transactions" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <span className="nav-icon">📋</span>
        <span className="nav-label">{t('nav.history')}</span>
      </NavLink>

      {/* Analytics - Center (FAB handles adding, no need for Add button here) */}
      <NavLink to="/analytics" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <span className="nav-icon">📈</span>
        <span className="nav-label">{t('nav.analytics')}</span>
      </NavLink>

      <NavLink to="/overview" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <span className="nav-icon">📊</span>
        <span className="nav-label">{t('nav.overview')}</span>
      </NavLink>

      {/* More menu */}
      <button className="nav-item" onClick={onMoreClick} aria-label={t('nav.more_options_aria')}>
        <span className="nav-icon">☰</span>
        <span className="nav-label">{t('nav.more')}</span>
      </button>
    </nav>
  );
}

// Floating Action Button
function FAB({ onClick }: { onClick: () => void }) {
  const { t } = useLanguage();
  const location = useLocation();
  const label = t(quickAddLabelKey(location.pathname));
  return (
    <button className="fab" onClick={onClick} aria-label={label} title={label}>
      +
    </button>
  );
}

// Loading Spinner
function LoadingScreen() {
  const { t } = useLanguage();
  return (
    <div className="loading-screen" style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      background: 'var(--bg-void)',
      color: 'var(--text-muted)',
      fontSize: 'var(--fs-lg)',
    }}>
      {t('common.loading')}
    </div>
  );
}

// Error Fallback (shown when app crashes)
function ErrorFallback() {
  const { t } = useLanguage();
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      background: 'var(--bg-void, #0d1117)',
      color: 'var(--text-primary, #c9d1d9)',
      fontFamily: 'system-ui, sans-serif',
      textAlign: 'center',
      padding: '2rem',
    }}>
      <h1 style={{ color: 'var(--error, #f85149)', marginBottom: '1rem' }}>
        {t('errors.crash_title')}
      </h1>
      <p style={{ maxWidth: '400px', marginBottom: '1.5rem', color: 'var(--text-muted, #8b949e)' }}>
        {t('errors.crash_body')}
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '0.75rem 1.5rem',
          background: 'var(--accent, #238636)',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '1rem',
        }}
      >
        {t('errors.reload_app')}
      </button>
      <a
        href="https://github.com/TakashiKyoto/NinjaCash/issues"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          marginTop: '1.5rem',
          color: 'var(--text-muted, #8b949e)',
          fontSize: '0.875rem',
        }}
      >
        {t('errors.report_github')}
      </a>
    </div>
  );
}

// Main App
export default function App() {
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { isOwner } = useOwnerAccess();

  // ALL HOOKS MUST BE BEFORE CONDITIONAL RETURNS (React rules)
  // Close modals on navigation
  useEffect(() => {
    setShowQuickAdd(false);
    setShowAddAccount(false);
    setShowMoreMenu(false);
  }, [location.pathname]);

  // BUG-058/BUG-059 FIX: One-time startup reconciliation
  // Fixes any balance discrepancies from before these bug fixes were deployed
  // Only runs once per device (tracked in localStorage)
  useEffect(() => {
    const runStartupReconciliation = async () => {
      const RECONCILE_VERSION = 'balance-fix-v1'; // Bump this to force re-run
      const hasRunReconciliation = localStorage.getItem(RECONCILE_VERSION);

      if (!hasRunReconciliation && isAuthenticated) {
        try {
          const correctedCount = await accountService.reconcileBalancesAfterSync();
          if (correctedCount > 0) {
            console.log(`[STARTUP] Fixed ${correctedCount} account balance discrepancies`);
          }
          localStorage.setItem(RECONCILE_VERSION, new Date().toISOString());
        } catch (error) {
          console.error('[STARTUP] Balance reconciliation failed:', error);
          // Don't block app - try again next startup
        }
      }
    };

    runStartupReconciliation();
  }, [isAuthenticated]);

  // RECURRING-FIX: Issue 4C - One-time cleanup for duplicate scheduled transactions
  // Removes duplicates with same detectionKey (legacy data from before fix 4A)
  // Only runs once per device (tracked in localStorage)
  useEffect(() => {
    const runScheduleDedup = async () => {
      const DEDUPE_VERSION = 'schedule-dedupe-v1'; // Bump this to force re-run
      const hasRunDedupe = localStorage.getItem(DEDUPE_VERSION);

      if (!hasRunDedupe && isAuthenticated) {
        try {
          const removedCount = await scheduledTransactionService.dedupeByDetectionKey();
          if (removedCount > 0) {
            console.log(`[STARTUP] Removed ${removedCount} duplicate scheduled transactions`);
          }
          localStorage.setItem(DEDUPE_VERSION, new Date().toISOString());
        } catch (error) {
          console.error('[STARTUP] Schedule dedupe failed:', error);
          // Don't block app - try again next startup
        }
      }
    };

    runScheduleDedup();
  }, [isAuthenticated]);

  const handleQuickAddSuccess = useCallback(() => {
    // Trigger page refresh after adding transaction
    // Pages will re-fetch their data via hooks
    console.log('[App] Transaction added successfully');
  }, []);

  const handleQuickAddClick = useCallback(() => {
    // QoL: FAB adds what the current page manages
    if (location.pathname === '/accounts') {
      setShowAddAccount(true);
    } else if (location.pathname === '/categories') {
      // CategoriesPage owns the Add Category modal (it knows the active
      // expense/income tab) - signal it instead of mounting a second copy
      window.dispatchEvent(new CustomEvent('njc:add-category'));
    } else {
      setShowQuickAdd(true);
    }
  }, [location.pathname]);

  const handleMoreClick = useCallback(() => {
    setShowMoreMenu(prev => !prev); // Toggle - tap again to close
  }, []);

  // Show loading screen while checking auth
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Public routes - accessible without authentication (legal compliance)
  if (location.pathname === '/privacy') {
    return <PrivacyPage />;
  }
  if (location.pathname === '/terms') {
    return <TermsPage />;
  }

  // Showcase hub + Depth Cinema winners (others archived - see SHOWCASE.md)
  if (location.pathname === '/xp1') {
    return <Xp1LandingIndex />;
  }
  if (location.pathname === '/xp1h-d') {
    return <Xp1HybridD />;
  }
  if (location.pathname === '/xp1h-d2') {
    return <Xp1HybridDv2 />;
  }
  if (location.pathname === '/xp1h-d3') {
    return <Xp1HybridDv3 />;
  }

  // Features page (public - no auth required)
  if (location.pathname === '/features') {
    return <FeaturesPage />;
  }
  if (location.pathname === '/features-a') {
    return <Features_BentoA />; // Original experiment kept for reference
  }

  // Landing page preview (for dev - view landing while logged in)
  if (location.pathname === '/landing') {
    return <LoginPage />;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <div className="app">
      {/* Sidebar (Desktop only - shown via CSS media query) */}
      <Sidebar onQuickAddClick={handleQuickAddClick} isOwner={isOwner} />

      {/* Main Content */}
      <main className="main">
        <Routes>
          {/* Default redirect to accounts (home) */}
          <Route path="/" element={<Navigate to="/accounts" replace />} />

          {/* All pages implemented */}
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/overview" element={<OverviewPage />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/crypto" element={<CryptoPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/import" element={<ImportDataPage />} />

          {/* Dev/Debug pages (hidden in production) */}
          <Route path="/dev" element={<DevPage />} />
          <Route path="/metrics_dev" element={<MetricsDevPage />} />
          <Route path="/log" element={<LogPage />} />

          {/* More menu (mobile) */}
          <Route path="/more" element={<MorePage />} />

          {/* Upgrade funnel pages */}
          <Route path="/pricing" element={<PricingPageB />} />
          <Route path="/pricing-b" element={<PricingPageB />} /> {/* Legacy route */}
          <Route path="/funnel-preview" element={<FunnelPreview />} /> {/* Dev only */}

          {/* 404 */}
          <Route path="*" element={<PlaceholderPage title="Page Not Found" />} />
        </Routes>
      </main>

      {/* Floating Action Button */}
      <FAB onClick={handleQuickAddClick} />

      {/* Quick Add Modal */}
      <QuickAddModal
        isOpen={showQuickAdd}
        onClose={() => setShowQuickAdd(false)}
        onSuccess={handleQuickAddSuccess}
      />

      {/* Add Account Modal (shown when FAB clicked on /accounts) */}
      <AddAccountModal
        isOpen={showAddAccount}
        onClose={() => setShowAddAccount(false)}
        onSuccess={() => setShowAddAccount(false)}
      />

      {/* More Menu (slide-up bottom sheet) */}
      <MoreMenu
        isOpen={showMoreMenu}
        onClose={() => setShowMoreMenu(false)}
      />

      {/* Bottom navigation (Mobile only - hidden via CSS on desktop) */}
      <BottomNav onMoreClick={handleMoreClick} />

      {/* Authenticated owner metrics ingestion */}
      <MetricsTracker />
    </div>
    </Sentry.ErrorBoundary>
  );
}
